import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Image from 'next/image';
import { useAtomValue } from 'jotai';
import DocumentAgentIcon from '@/components/Document/DocumentAgentIcon';
import AiChatView, {
  AiChatAssistantMessage,
  AiChatMessage,
} from '@/components/@IntranetLibrary/AiChatView';
import AiStarIcon from '@/public/icons/ai-star.png';
import CloseIcon from '@/public/icons/close_white_24.svg';
import { useDialog } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { ChatProps } from './types';
import AnswerActions from './components/AnswerActions';
import { useFileChat } from './useFileChat';

const Chat = ({
  isMobile = false,
  fileUuid,
  fileName,
  suggestions = [],
  sx,
  onCloseList,
}: ChatProps) => {
  const { passportId } = useAtomValue(MyInfoState);
  const dialog = useDialog();

  const { messages, isSubmitting, sendMessage, resetChat } = useFileChat(
    fileUuid,
    passportId,
  );

  useEffect(() => {
    resetChat();
  }, [fileUuid, passportId, resetChat]);

  // 지울 대화가 없거나 답변 생성 중이면 초기화 불가 (회의록과 동일 규칙)
  const resetDisabled = messages.length === 0 || isSubmitting;

  const handleReset = () => {
    dialog.show({
      message: '채팅을 초기화할까요?\n지금까지의 채팅 내용이 모두 삭제됩니다.',
      confirmButtonText: '초기화',
      cancelButtonText: '취소',
      onConfirm: resetChat,
      // 컨펌창 기본 z-index(1300)가 AI Chat 바텀시트(zIndex 1350)보다 낮아
      // 시트 후방에 깔리는 문제가 있어 최상위 z-index로 띄운다.
      forceToFront: true,
    });
  };

  // 헤더 타이틀 아이콘 — Figma AI Magic = 답변 아바타와 동일한 ai-star.png 이미지
  const titleIcon = <Image src={AiStarIcon} width={24} height={24} alt="" />;

  // useFileChat 는 파일 진입 시마다 비어 있고 실시간 전송분만 쌓이므로
  // 모든 메시지를 새 메시지로 간주(타이핑/버블 애니메이션 부여).
  const chatMessages: AiChatMessage[] = messages.map((m) =>
    m.role === 'user'
      ? { kind: 'user', id: m.id, text: m.content, isAnimated: true }
      : {
          kind: 'assistant',
          id: m.id,
          text: m.content,
          status: m.status ?? 'done',
          isAnimated: true,
          meta: { question: m.question },
        },
  );

  const renderActions = (m: AiChatAssistantMessage) => (
    <AnswerActions
      content={m.text}
      question={m.meta?.question as string | undefined}
      fileName={fileName}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        ...sx,
      }}
    >
      <AiChatView
        variant="dark"
        isMobile={isMobile}
        header={
          isMobile
            ? {
                variant: 'mobile',
                title: 'AI Chat',
                titleIcon,
                onClose: onCloseList,
                onReset: handleReset,
                resetDisabled,
                // 모바일 뒤로가기: 우향 이중 꺾쇠 »(Figma MO 헤더, 28px)
                closeIcon: (
                  <svg width={28} height={28} viewBox="0 0 28 28" fill="none">
                    <path
                      d="M14.875 20.125L20.125 14L14.875 7.875"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.875 20.125L13.125 14L7.875 7.875"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              }
            : {
                variant: 'full',
                title: 'AI Chat',
                titleIcon,
                onClose: onCloseList,
                onReset: handleReset,
                resetDisabled,
                closeIcon: <CloseIcon width={24} height={24} />,
              }
        }
        data={{
          messages: chatMessages,
          isSending: isSubmitting,
          onSend: sendMessage,
        }}
        greeting={{ sub: '만나서 반갑습니다!', main: '무엇을 도와드릴까요?' }}
        disclaimer="AI 분석 결과는 실제 내용과 다를 수 있으니 결과를 확인해 주세요."
        placeholder="파일에 대해 물어보세요"
        suggestions={suggestions}
        emptyIcon={<DocumentAgentIcon width={80} height={80} transparent />}
        renderAvatar={(isStreaming) =>
          isStreaming ? (
            <DocumentAgentIcon width={28} height={28} transparent />
          ) : (
            <Image src={AiStarIcon} width={28} height={28} alt="AI" />
          )
        }
        renderActions={renderActions}
      />
    </Box>
  );
};

export default Chat;
